/**
 * Spinner — thin ring loading indicator.
 *
 * Matches the spinner pattern used in LTI pages (border-based ring):
 *   • Track:   border-webex-gray-200 dark:border-webex-gray-850
 *   • Head:    border-t-webex-gray-1000 dark:border-t-white
 *   • Stroke:  2–4px depending on size
 *
 * Rotates via animate-spin; respects prefers-reduced-motion.
 */
import { cn } from "@lib/cn"

export type SpinnerSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl"

interface SpinnerProps {
  className?: string
  size?: SpinnerSize
  ariaLabel?: string
}

const sizeMap: Record<SpinnerSize, string> = {
  xs:  "w-4 h-4 border-2",
  sm:  "w-6 h-6 border-2",
  md:  "w-8 h-8 border-2",
  lg:  "w-10 h-10 border-2",
  xl:  "w-12 h-12 border-[3px]",
  "2xl": "w-16 h-16 border-[3px]",
  "3xl": "w-20 h-20 border-4",
  "4xl": "w-24 h-24 border-4",
}

export function Spinner({ className, size = "md", ariaLabel = "Loading…" }: SpinnerProps) {
  return (
    <>
      <div
        aria-hidden="true"
        className={cn(
          sizeMap[size],
          "rounded-full animate-spin motion-reduce:animate-none",
          "border-webex-gray-200 border-t-webex-gray-1000",
          "dark:border-webex-gray-850 dark:border-t-white",
          className,
        )}
      />
      <span className="sr-only">{ariaLabel}</span>
    </>
  )
}
